import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { useProAccess } from './useProAccess';

export interface QuestionAssistant {
  id: string;
  question: string;
  reponse: string | null;
  created_at: string;
}

export const useAssistantNautique = () => {
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState<QuestionAssistant[]>([]);
  const { user } = useAuth();
  const { isProAccount, isDemoAccount } = useProAccess();

  const poserQuestion = async (
    question: string,
    conversationHistory?: Array<{ role: string; content: string }>,
    imageBase64?: string
  ): Promise<{ reponse: string | null; limitReached: boolean; actionExecuted?: boolean }> => {
    if (!user) {
      toast({
        title: "Erreur",
        description: "Vous devez être connecté pour utiliser l'assistant.",
        variant: "destructive",
      });
      return { reponse: null, limitReached: false };
    }

    setLoading(true);
    try {
      const body: any = { question };
      if (conversationHistory && conversationHistory.length > 0) {
        body.conversation_history = conversationHistory;
      }
      if (imageBase64) {
        body.image_base64 = imageBase64;
      }
      
      const { data, error } = await supabase.functions.invoke('assistant-nautique', {
        body
      });

      // Cas attendu: quota atteint (retourné en 200 par l'Edge Function)
      if (data?.limit_reached === true || data?.error === 'Limite quotidienne atteinte') {
        toast({
          title: "Quota journalier atteint",
          description: data?.message || "Vous avez atteint la limite de 1 question gratuite aujourd'hui. Passez à l'abonnement Premium pour un accès illimité.",
          variant: "destructive",
        });
        return { reponse: null, limitReached: true };
      }

      if (error) {
        // On évite de logguer en console pour les erreurs "non-2xx" car elles peuvent être
        // interprétées comme des erreurs runtime côté UI (et le quota est géré via data.limit_reached).
        
        toast({
          title: "Erreur",
          description: "Impossible de contacter l'assistant nautique.",
          variant: "destructive",
        });
        return { reponse: null, limitReached: false };
      }

      if (data?.error) {
        toast({
          title: "Erreur",
          description: data.error,
          variant: "destructive",
        });
        return { reponse: null, limitReached: false };
      }

      // Ajouter la nouvelle question/réponse à la liste
      const nouvelleQuestion: QuestionAssistant = {
        id: Date.now().toString(),
        question,
        reponse: data.reponse,
        created_at: new Date().toISOString()
      };

      setQuestions(prev => [nouvelleQuestion, ...prev]);

      return { reponse: data.reponse, limitReached: false, actionExecuted: data.action_executed || false };
    } catch (err) {
      console.error('Erreur inattendue assistant:', err);
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite.",
        variant: "destructive",
      });
      return { reponse: null, limitReached: false };
    } finally {
      setLoading(false);
    }
  };

  const chargerHistorique = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('questions_assistant')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Erreur lors du chargement de l\'historique:', error);
        return;
      }

      setQuestions(data || []);
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const obtenirUsageQuotidien = async () => {
    const FREE_LIMIT = 1;
    const PREMIUM_LIMIT = 20;
    
    if (!user) return { questions_count: 0, questions_restantes: isProAccount ? PREMIUM_LIMIT : FREE_LIMIT };

    try {
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('assistant_usage')
        .select('questions_count')
        .eq('user_id', user.id)
        .eq('date_usage', today)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Erreur lors de la récupération de l\'usage:', error);
        return { questions_count: 0, questions_restantes: isProAccount ? PREMIUM_LIMIT : FREE_LIMIT };
      }

      const questions_count = data?.questions_count || 0;
      const limit = isProAccount ? PREMIUM_LIMIT : FREE_LIMIT;
      return {
        questions_count,
        questions_restantes: Math.max(0, limit - questions_count)
      };
    } catch (err) {
      console.error('Erreur:', err);
      return { questions_count: 0, questions_restantes: isProAccount ? PREMIUM_LIMIT : FREE_LIMIT };
    }
  };

  const supprimerConversations = async (ids: string[]) => {
    if (!user || ids.length === 0) return false;

    try {
      // Séparer les IDs locaux (timestamps) des vrais UUIDs de la base de données
      const isValidUUID = (id: string) => {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return uuidRegex.test(id);
      };

      const dbIds = ids.filter(isValidUUID);
      const localIds = ids.filter(id => !isValidUUID(id));

      // Supprimer les entrées de la base de données si elles existent
      if (dbIds.length > 0) {
        const { error } = await supabase
          .from('questions_assistant')
          .delete()
          .eq('user_id', user.id)
          .in('id', dbIds);

        if (error) {
          console.error('Erreur lors de la suppression:', error);
          toast({
            title: "Erreur",
            description: "Impossible de supprimer les conversations.",
            variant: "destructive",
          });
          return false;
        }
      }

      // Mettre à jour la liste locale (supprime à la fois les IDs de BDD et locaux)
      setQuestions(prev => prev.filter(q => !ids.includes(q.id)));
      
      toast({
        title: "Succès",
        description: `${ids.length} conversation(s) supprimée(s).`,
      });
      
      return true;
    } catch (err) {
      console.error('Erreur:', err);
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite.",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    loading,
    questions,
    poserQuestion,
    chargerHistorique,
    obtenirUsageQuotidien,
    supprimerConversations,
    isDemoAccount
  };
};
